import React, { useState, useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { fetchCustomers } from "../../redux/actions/CustomerActions";
import { fetchCustomerTypes } from "../../redux/actions/CustomerTypeActions";
import { displayDateFormart, isValidArr } from "../../utility/utils";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import CustomerForm from "./CustomerForm";
import Loader from "../../components/loader/Loader";
import { CustomerModel } from "../../models/CustomerModel";

export type Mode = "add" | "edit";

type iData = {
  id: number;
  name: string;
  description: string;
  customerType: string;
  createdDate: string;
  // status: string;
};

const CustomerPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<CustomerModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allData = useAppSelector((state) => state.customer.all_Customers);
  const customerTypes = useAppSelector(
    (state) => state.customerType.all_CustomerTypes
  );

  const isCustomersFetching = useAppSelector(
    (state) => state.customer.isCustomersFetching
  );

  const columns = useMemo(() => {
    return [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "description",
        numeric: false,
        disablePadding: false,
        label: "Description",
      },
      {
        id: "customerType",
        numeric: false,
        disablePadding: false,
        label: "Customer Type",
        width: 200,
      },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 200,
      },
      // {
      //   id: "status",
      //   numeric: false,
      //   disablePadding: false,
      //   label: "Status",
      // },
    ];
  }, []);

  //To fetch all customers and customer types
  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchCustomerTypes());
  }, [dispatch]);

  useEffect(() => {
    setRootData(() =>
      allData.map((rec) => {
        const customerType = customerTypes.find(
          (cType) => cType.customerTypeId === rec.customerTypeId
        );
        return {
          id: rec.customerId,
          name: rec.name,
          description: rec.description,
          customerType: customerType?.name || "",
          createdDate: displayDateFormart(rec.createdDate),
          // status: rec.isActive ? "Active" : "Inactive",
        };
      })
    );
  }, [allData, customerTypes]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editRec = allData.find((rec) => rec.customerId === id);
    setMode("edit");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allData) && isCustomersFetching ? <Loader /> : null}
      <PageTitle
        title="Customers"
        btnText="Add Customer"
        onClick={createHandler}
      />
      <PageSearch
        placeholder="Search Customers"
        searchHandler={searchHandler}
      />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={mode === "add" ? "Create Customer" : "Edit Customer"}
          handleModalClose={handleModalClose}
        >
          <CustomerForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default CustomerPage;
